import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit'
import { client } from '../../utils/client'

// Async API (Thunks) - used with useDispatch
export const getShopItemGroups = createAsyncThunk(
  'shopItemGroup/getShopItemGroups',
  async (itemCategory, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      // Get shopUsername from auth user (primary) or user profile (fallback)
      const shopUsername = state.auth?.user?.username || state.user?.profile?.username

      if (!shopUsername) {
        return rejectWithValue({
          status: 400,
          message: 'Shop username not found',
        })
      }

      // itemCategory is optional - if not provided, fetch all groups
      const params = itemCategory ? { itemCategory } : {}
      const response = await client.get(`/shop/item-groups/${shopUsername}`, { params })

      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const createShopItemGroup = createAsyncThunk(
  'shopItemGroup/createShopItemGroup',
  async ({ itemCategory, groupData }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      // Get shopUsername from auth user (primary) or user profile (fallback)
      const shopUsername = state.auth?.user?.username || state.user?.profile?.username

      if (!shopUsername) {
        return rejectWithValue({
          status: 400,
          message: 'Shop username not found',
        })
      }

      const response = await client.post(`/shop/item-groups/${shopUsername}`, {
        itemCategory,
        ...groupData,
      })

      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const updateShopItemGroup = createAsyncThunk(
  'shopItemGroup/updateShopItemGroup',
  async ({ privateItemGroupNumber, groupData }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      // Get shopUsername from auth user (primary) or user profile (fallback)
      const shopUsername = state.auth?.user?.username || state.user?.profile?.username

      if (!shopUsername) {
        return rejectWithValue({
          status: 400,
          message: 'Shop username not found',
        })
      }

      const response = await client.put(
        `/shop/item-groups/${shopUsername}/${privateItemGroupNumber}`,
        groupData,
      )

      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const deleteShopItemGroup = createAsyncThunk(
  'shopItemGroup/deleteShopItemGroup',
  async (privateItemGroupNumber, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      // Get shopUsername from auth user (primary) or user profile (fallback)
      const shopUsername = state.auth?.user?.username || state.user?.profile?.username

      if (!shopUsername) {
        return rejectWithValue({
          status: 400,
          message: 'Shop username not found',
        })
      }

      const response = await client.delete(
        `/shop/item-groups/${shopUsername}/${privateItemGroupNumber}`,
      )

      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Slice
export const shopItemGroupSlice = createSlice({
  name: 'shopItemGroup',
  initialState: {
    groups: [],
    getShopItemGroups: {
      status: null,
      error: null,
    },
    createShopItemGroup: {
      status: null,
      error: null,
    },
    updateShopItemGroup: {
      status: null,
      error: null,
    },
    deleteShopItemGroup: {
      status: null,
      error: null,
    },
  },
  reducers: {
    clearStatus: (state) => {
      for (const key in state) {
        if (state[key]?.status) {
          state[key].status = null
        }
        if (state[key]?.error) {
          state[key].error = null
        }
      }
    },
    clearGroups: (state) => {
      state.groups = []
    },
  },
  extraReducers: (builder) => {
    builder
      // getShopItemGroups
      .addCase(getShopItemGroups.pending, (state) => {
        state.getShopItemGroups.status = 'loading'
      })
      .addCase(getShopItemGroups.fulfilled, (state, action) => {
        state.getShopItemGroups.status = 'succeeded'
        state.getShopItemGroups.error = null

        if (action.payload.data.success) {
          state.groups = action.payload.data.data || []
        }
      })
      .addCase(getShopItemGroups.rejected, (state, action) => {
        state.getShopItemGroups.status = 'failed'
        state.getShopItemGroups.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
        state.groups = []
      })
      // createShopItemGroup
      .addCase(createShopItemGroup.pending, (state) => {
        state.createShopItemGroup.status = 'loading'
      })
      .addCase(createShopItemGroup.fulfilled, (state, action) => {
        state.createShopItemGroup.status = 'succeeded'
        state.createShopItemGroup.error = null

        if (action.payload.data.success && action.payload.data.data) {
          state.groups.push(action.payload.data.data)
        }
      })
      .addCase(createShopItemGroup.rejected, (state, action) => {
        state.createShopItemGroup.status = 'failed'
        state.createShopItemGroup.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // updateShopItemGroup
      .addCase(updateShopItemGroup.pending, (state) => {
        state.updateShopItemGroup.status = 'loading'
      })
      .addCase(updateShopItemGroup.fulfilled, (state, action) => {
        state.updateShopItemGroup.status = 'succeeded'
        state.updateShopItemGroup.error = null

        if (action.payload.data.success && action.payload.data.data) {
          const index = state.groups.findIndex((g) => g._id === action.payload.data.data._id)
          if (index !== -1) {
            state.groups[index] = action.payload.data.data
          }
        }
      })
      .addCase(updateShopItemGroup.rejected, (state, action) => {
        state.updateShopItemGroup.status = 'failed'
        state.updateShopItemGroup.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // deleteShopItemGroup
      .addCase(deleteShopItemGroup.pending, (state) => {
        state.deleteShopItemGroup.status = 'loading'
      })
      .addCase(deleteShopItemGroup.fulfilled, (state, action) => {
        state.deleteShopItemGroup.status = 'succeeded'
        state.deleteShopItemGroup.error = null

        if (action.payload.data.success && action.payload.data.data?.privateItemGroupNumber) {
          state.groups = state.groups.filter(
            (g) => g.privateItemGroupNumber !== action.payload.data.data.privateItemGroupNumber,
          )
        }
      })
      .addCase(deleteShopItemGroup.rejected, (state, action) => {
        state.deleteShopItemGroup.status = 'failed'
        state.deleteShopItemGroup.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
  },
})

export const { clearStatus, clearGroups } = shopItemGroupSlice.actions

// Selectors
export const selectShopItemGroups = (state) => state.shopItemGroup.groups

// Memoized selector to prevent unnecessary rerenders
export const selectShopItemGroupsByCategory = createSelector(
  [selectShopItemGroups, (state, itemCategory) => itemCategory],
  (groups, itemCategory) => groups.filter((g) => g.itemCategory === itemCategory),
)

export default shopItemGroupSlice.reducer
